// ======================================================================
// 📁 File: src/modules/ppe/pages/resources/tool/ToolDrawer.jsx
// 📝 Purpose:
//   Tool Master – Create / Edit Drawer
//   - Tool Type (lookup-driven)
//   - Shared flag
//   - Backend-driven lifecycle
// ======================================================================

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../../api/axiosClient";

// ----------------------------------------------------------------------
export default function ToolDrawer({
  open,
  mode,
  record,
  toolTypes = [],
  onClose,
  onSaved,
}) {
  const isEdit = mode === "edit";

  const [form, setForm] = useState({
    tool_type_id: "",
    tool_code: "",
    tool_name: "",
    description: "",
    is_shared: false,
    is_active: true,
  });

  const [saving, setSaving] = useState(false);

  // ------------------------------------------------------------
  // Populate edit data
  // ------------------------------------------------------------
  useEffect(() => {
    if (isEdit && record) {
      setForm({
        tool_type_id: record.tool_type_id || "",
        tool_code: record.tool_code || "",
        tool_name: record.tool_name || "",
        description: record.description || "",
        is_shared: !!record.is_shared,
        is_active: record.is_active,
      });
    }
  }, [isEdit, record]);

  // ------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------
  const update = (key, value) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    // ✅ Basic validation
    if (!form.tool_type_id || !form.tool_code || !form.tool_name) {
      alert("Tool Type, Code and Name are required");
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await axiosClient.put(
          `/api/ppe/masters/tools/${record.id}`,
          {
            tool_type_id: form.tool_type_id,
            tool_name: form.tool_name,
            description: form.description || null,
            is_shared: form.is_shared,
            is_active: form.is_active,
          }
        );
      } else {
        await axiosClient.post("/api/ppe/masters/tools", {
          tool_type_id: form.tool_type_id,
          tool_code: form.tool_code,
          tool_name: form.tool_name,
          description: form.description || null,
          is_shared: form.is_shared,
        });
      }

      onSaved?.();
      onClose();
    } catch (err) {
      console.error("Tool save failed", err);
      alert("Failed to save tool");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div className="drawer-backdrop">
      <div className="drawer drawer-right">
        {/* ====================================================== */}
        {/* Header */}
        {/* ====================================================== */}
        <div className="drawer-header">
          <h5 style={{ margin: 0 }}>
            {isEdit ? "Edit Tool" : "Create Tool"}
          </h5>
          <button className="btn-close" onClick={onClose} />
        </div>

        {/* ====================================================== */}
        {/* Body */}
        {/* ====================================================== */}
        <div className="drawer-body">
          {/* Tool Type */}
          <div className="mb-3">
            <label className="form-label">Tool Type *</label>
            <select
              className="form-select"
              value={form.tool_type_id}
              onChange={(e) =>
                update("tool_type_id", e.target.value)
              }
            >
              <option value="">Select Tool Type</option>
              {toolTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.lookup_name}
                </option>
              ))}
            </select>
          </div>

          {/* Code */}
          <div className="mb-3">
            <label className="form-label">Tool Code *</label>
            <input
              className="form-control"
              value={form.tool_code}
              disabled={isEdit}
              onChange={(e) =>
                update("tool_code", e.target.value)
              }
            />
          </div>

          {/* Name */}
          <div className="mb-3">
            <label className="form-label">Tool Name *</label>
            <input
              className="form-control"
              value={form.tool_name}
              onChange={(e) =>
                update("tool_name", e.target.value)
              }
            />
          </div>

          {/* Description */}
          <div className="mb-3">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows={3}
              value={form.description}
              onChange={(e) =>
                update("description", e.target.value)
              }
            />
          </div>

          {/* Shared */}
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              checked={form.is_shared}
              onChange={(e) =>
                update("is_shared", e.target.checked)
              }
            />
            <label className="form-check-label">
              Shared Tool (usable across machines)
            </label>
          </div>

          {/* Active (edit only) */}
          {isEdit && (
            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                checked={form.is_active}
                onChange={(e) =>
                  update("is_active", e.target.checked)
                }
              />
              <label className="form-check-label">
                Active
              </label>
            </div>
          )}
        </div>

        {/* ====================================================== */}
        {/* Footer */}
        {/* ====================================================== */}
        <div
          style={{
            padding: 16,
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button
            className="btn btn-outline-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
